import React from "react";
import {
  Box,
  Text,
  Button,
  HStack,
  Link,
  Flex,
  PinInput,
  PinInputField,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";

interface CodeVerificationFormProps {
  phoneData: { phone?: string; code?: string };
  enteredCode: string;
  setEnteredCode: (code: string) => void;
  isCodeValid: boolean;
  resendTimer: number;
  isBlocked: boolean;
  isLoading: boolean;
  onBack: () => void;
  onSubmit: () => void;
  onResendCode: () => void;
}

export const CodeVerificationForm: React.FC<CodeVerificationFormProps> = ({
  phoneData,
  enteredCode,
  setEnteredCode,
  isCodeValid,
  resendTimer,
  isBlocked,
  isLoading,
  onBack,
  onSubmit,
  onResendCode,
}) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "userPhoneEmailVerify",
  });

  return (
    <Box display={"flex"} flexDir="column" pt="24px" gap="24px">
      <Box>
        <Text fontSize="sm">{t("weSentATextToYourPhone")}</Text>
        <Text fontWeight="bold" fontSize="md">
          +{phoneData.code} {phoneData.phone}
        </Text>
      </Box>

      <HStack justify="center">
        <PinInput
          otp
          onChange={(value) => {
            setEnteredCode(value);
          }}
          value={enteredCode}
          isDisabled={isBlocked}
        >
          <PinInputField autoFocus />
          <PinInputField />
          <PinInputField />
          <PinInputField />
          <PinInputField />
          <PinInputField />
        </PinInput>
      </HStack>

      {!isCodeValid && (
        <Text fontSize={"xs"} color="red.400" textAlign="center">
          {t("invalidCode")}
        </Text>
      )}

      {isBlocked && (
        <Text fontSize={"xs"} color="red.400" textAlign="center">
          {t("maximumAttempts")}
        </Text>
      )}

      {!isBlocked && (
        <Link
          color={resendTimer > 0 ? "gray.400" : "blue.500"}
          fontSize="sm"
          textAlign="center"
          cursor={resendTimer > 0 ? "not-allowed" : "pointer"}
          onClick={() => {
            if (resendTimer === 0) onResendCode();
          }}
        >
          {resendTimer > 0
            ? `${t("resentCode")} (${resendTimer}s)`
            : t("resentCode")}
        </Link>
      )}

      <Flex justifyContent={"flex-end"}>
        <Button
          onClick={onBack}
          alignSelf="center"
          key="back"
          mr={3}
          color="newBlue.500"
          variant="ghost"
        >
          {t("back")}
        </Button>
        <Button
          layerStyle="ocean"
          _hover={{ layerStyle: "ocean" }}
          onClick={onSubmit}
          isDisabled={enteredCode.length < 6 || isLoading || isBlocked}
          isLoading={isLoading}
        >
          {t("confirmCode")}
        </Button>
      </Flex>
    </Box>
  );
};
